import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import { COMPARE_MODAL } from 'components/Catalog/ProductGrid/CompareModal/constants';
import { Text } from 'components/ui';
import Icon from 'components/Icon';
import EmptyStatePrompt from 'components/Catalog/ProductGrid/CompareModal/Zones/PickupAndDeliveryZone/EmptyStatePrompt';
import InStock from 'components/Catalog/ProductGrid/CompareModal/Zones/PickupAndDeliveryZone/InStock';
import OutOfStock from 'components/Catalog/ProductGrid/CompareModal/Zones/PickupAndDeliveryZone/OutOfStock';
import StatusLabel from 'components/Catalog/ProductGrid/CompareModal/Zones/PickupAndDeliveryZone/StatusLabel';

const DELIVERY_TYPES = {
    BOPIS: 'bopis',
    GIS: 'gis',
    SDD: 'sdd'
};

const FULFILLMENT_TYPE_MAP = {
    [DELIVERY_TYPES.BOPIS]: 'PICK',
    [DELIVERY_TYPES.GIS]: 'SHIP',
    [DELIVERY_TYPES.SDD]: 'SAMEDAY'
};

const PickupAndDeliveryZone = ({
    skus = [],
    availabilities = [],
    getText = () => { },
    storeName,
    zipCode,
    hasPreferredStore,
    showStoreSwitcherModal,
    showShippingDeliveryLocationModal,
    saveScrollPosition
}) => {
    const hasPreferredZip = Boolean(zipCode);

    const handleOpenStoreModal = e => {
        e.preventDefault();
        e.stopPropagation();
        saveScrollPosition?.();

        showStoreSwitcherModal({
            isOpen: true,
            showStoreDetails: true,
            entry: 'product compare tool',
            componentName: COMPARE_MODAL
        });
    };

    const handleOpenShippingDeliveryLocationModal = e => {
        e.preventDefault();
        e.stopPropagation();
        saveScrollPosition?.();

        showShippingDeliveryLocationModal({
            isOpen: true
        });
    };

    const isDeliverySubsectionLocationMissing = deliveryType => {
        if (deliveryType === DELIVERY_TYPES.BOPIS) {
            return !hasPreferredStore || !storeName;
        }

        return !hasPreferredZip;
    };

    const getEmptyStateHandler = deliveryType =>
        deliveryType === DELIVERY_TYPES.BOPIS ? handleOpenStoreModal : handleOpenShippingDeliveryLocationModal;

    const renderSubsectionTitle = type => {
        if (isDeliverySubsectionLocationMissing(type)) {
            return null;
        }

        const showChangeStoreModal = type === DELIVERY_TYPES.BOPIS;
        const zip = zipCode ? zipCode : getText('setLocation');

        return (
            <Text
                is='button'
                lineHeight={'tight'}
                onClick={e => (showChangeStoreModal ? handleOpenStoreModal(e) : handleOpenShippingDeliveryLocationModal(e))}
            >
                {getText(type)}
                <Text
                    is='span'
                    fontWeight='bold'
                >
                    {showChangeStoreModal ? storeName : zip}
                </Text>
                <Icon
                    name='caretDown'
                    size={12}
                    marginLeft={1}
                />
            </Text>
        );
    };

    const renderEmptyStateRow = deliveryType => () => (
        <EmptyStatePrompt
            onClick={getEmptyStateHandler(deliveryType)}
            getText={getText}
            deliveryType={deliveryType}
        />
    );

    const renderSubsectionContent = fulfillmentType => (sku, index) => {
        if (sku?.onlineOnly && fulfillmentType !== FULFILLMENT_TYPE_MAP[DELIVERY_TYPES.GIS]) {
            return (
                <StatusLabel
                    textKey='onlineOnly'
                    getText={getText}
                />
            );
        }

        if (sku?.storeOnly && fulfillmentType === FULFILLMENT_TYPE_MAP[DELIVERY_TYPES.GIS]) {
            return (
                <StatusLabel
                    textKey='storeOnly'
                    getText={getText}
                />
            );
        }

        const availabilityDetails = availabilities[index];
        const fulfillmentMatch = availabilityDetails?.availabilityByFT?.find(ft => ft.fulfillmentType === fulfillmentType);

        if (fulfillmentType === FULFILLMENT_TYPE_MAP[DELIVERY_TYPES.SDD] && !fulfillmentMatch) {
            return (
                <StatusLabel
                    textKey='notAvailable'
                    getText={getText}
                />
            );
        }

        const atpStatus = fulfillmentMatch?.locations?.[0]?.atpStatus ?? availabilityDetails?.atpStatus;

        const isOutOfStock = atpStatus === 'OOS';

        return isOutOfStock ? <OutOfStock getText={getText} /> : <InStock getText={getText} />;
    };

    const buildFulfillmentSubsection = deliveryType => {
        const fulfillmentType = FULFILLMENT_TYPE_MAP[deliveryType];
        const isLocationMissing = isDeliverySubsectionLocationMissing(deliveryType);

        if (isLocationMissing) {
            return {
                type: deliveryType,
                fullWidth: true,
                renderFullWidth: renderEmptyStateRow(deliveryType)
            };
        }

        return {
            type: deliveryType,
            renderTitle: renderSubsectionTitle,
            renderContent: renderSubsectionContent(fulfillmentType)
        };
    };

    const subsections = [
        buildFulfillmentSubsection(DELIVERY_TYPES.GIS),
        buildFulfillmentSubsection(DELIVERY_TYPES.SDD),
        buildFulfillmentSubsection(DELIVERY_TYPES.BOPIS)
    ];

    return (
        <CompareZoneBase
            skus={skus}
            getText={getText}
            titleKey='pickupAndDelivery'
            subsections={subsections}
        />
    );
};

PickupAndDeliveryZone.propTypes = {
    skus: PropTypes.arrayOf(PropTypes.object),
    availabilities: PropTypes.arrayOf(PropTypes.object),
    getText: PropTypes.func,
    storeName: PropTypes.string,
    zipCode: PropTypes.string,
    hasPreferredStore: PropTypes.bool,
    showStoreSwitcherModal: PropTypes.func.isRequired,
    showShippingDeliveryLocationModal: PropTypes.func.isRequired,
    saveScrollPosition: PropTypes.func
};

export default wrapFunctionalComponent(PickupAndDeliveryZone, 'PickupAndDeliveryZone');
